import { definedMap } from "@glideapps/ts-necessities";

import {
    ComponentKind,
    PropertyDescription,
    ComponentDescription,
    PropertyKind,
    InputOutputTables,
    MutatingScreenKind,
    TableColumn,
    TableGlideType,
    getNonHiddenColumns,
    isPrimitiveType,
    isMultiRelationType,
    arrayScreenName,
} from "../description";
import { ComponentHandlerBase } from "./handler";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import {
    ComponentDescriptor,
    ComponentBuilder,
    ArrayPropertyStyle,
    ComponentConfiguratorContext,
    SchemaInspector,
} from ".";
import { SerializedComponent } from "../../components/SerializedApp";
import { getArrayProperty, getSourceColumnProperty } from "./property-utils";
import { SimpleTableRowStyle } from "./simple-table-types";
import { mapFilterUndefined } from "../../Support";
import { getFeatureSetting } from "../../feature-settings";
import { fetchLinkColumn } from "../serialized-component";
import { makePushWebViewScreen, LinkAppearance, makeValueForSourceColumn, makeText } from "../builder-utils";
import { makeCaptionStringPropertyDescriptor, makeTextPropertyDescriptor } from "./descriptor-utils";
import { getColumnForSourceColumn } from "../source-column";

const ComponentKindSimpleTable: ComponentKind = "simple-table";

interface RowDescription {
    readonly caption: PropertyDescription;
    readonly value: PropertyDescription;
}

interface SimpleTableComponentDescription extends ComponentDescription {
    readonly caption: PropertyDescription | undefined;
    readonly footer: PropertyDescription | undefined;
    readonly rows: PropertyDescription;
}

function getColumnForRow(
    schema: SchemaInspector,
    tables: InputOutputTables,
    row: RowDescription
): TableColumn | undefined {
    const sc = getSourceColumnProperty(row.value);
    if (sc === undefined) return undefined;
    return getColumnForSourceColumn(schema, sc, tables.input, undefined);
}

// FIXME: We're repeating `value` here.  Bind it in a `let` and just refer to the var.
function getStyleForColumn(
    builder: ComponentBuilder,
    column: TableColumn | undefined,
    label: SerializedComponent,
    value: SerializedComponent
): {
    style: SimpleTableRowStyle;
    action: SerializedComponent | undefined;
    formattedValue: SerializedComponent | undefined;
} {
    const { b } = builder;

    switch (column?.type.kind) {
        case "image-uri":
            return { style: SimpleTableRowStyle.Image, action: undefined, formattedValue: undefined };

        case "uri":
        case "audio-uri":
            return {
                style: SimpleTableRowStyle.Link,
                action: makePushWebViewScreen(b, value, LinkAppearance.PageTitle),
                formattedValue: b.shortenLink(value),
            };

        case "phone-number":
            return { style: SimpleTableRowStyle.Link, action: b.phoneCall(value), formattedValue: undefined };

        case "email-address":
            return { style: SimpleTableRowStyle.Link, action: b.sendEmail(value), formattedValue: undefined };

        case "emoji":
            return { style: SimpleTableRowStyle.Emoji, action: undefined, formattedValue: undefined };

        case "boolean":
            return { style: SimpleTableRowStyle.Checkbox, action: undefined, formattedValue: undefined };

        case "array": {
            value = fetchLinkColumn(b, column, value, false);
            return {
                style: SimpleTableRowStyle.PushScreen,
                action: builder.makePushScreen(column.type, label, undefined, value),
                formattedValue: b.smartNumberOfItems(value, b.constant(null)),
            };
        }

        default:
            return { style: SimpleTableRowStyle.None, action: undefined, formattedValue: undefined };
    }
}

export class SimpleTableComponentHandler extends ComponentHandlerBase<SimpleTableComponentDescription> {
    constructor() {
        super(ComponentKindSimpleTable);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: SimpleTableComponentDescription,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _searchVariable: SerializedComponent | undefined,
        _searchNotActive: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const caption = makeText(builder, desc.caption, tables.input, true);
        const footer = makeText(builder, desc.footer, tables.input, true);

        const rowDescriptions = getArrayProperty<RowDescription>(desc.rows) ?? [];
        const rows = mapFilterUndefined(rowDescriptions, row => {
            let label = makeText(builder, row.caption, tables.input);
            let value = makeText(builder, row.value, tables.input, false, false);

            if (label === undefined && value === undefined) return undefined;

            label = label ?? b.constant("");
            value = value ?? b.constant("");

            const column = getColumnForRow(builder, tables, row);
            const valueWithFormat = definedMap(
                getSourceColumnProperty(row.value),
                sc =>
                    makeValueForSourceColumn(builder, sc, b.getContext, tables.input, undefined, undefined, true)?.value
            );

            const { style, action, formattedValue } = getStyleForColumn(builder, column, label, value);

            return b.object({
                label,
                // FIXME: We should always take valueWithFormat if the column actually
                // has a format, directly or indirectly.
                value: formattedValue ?? valueWithFormat ?? value,
                style: b.constant(style),
                action,
            });
        });

        if (rows.length === 0) return b.emptyUI;

        return b.ui("SimpleTable", { caption, footer, rows: b.list(rows) });
    }

    // NOTE: This needs to be synchronized with the code that handles the
    // items.
    public getScreensUsed(
        desc: SimpleTableComponentDescription,
        schema: SchemaInspector,
        tables: InputOutputTables | undefined
    ): readonly string[] {
        if (tables === undefined) return [];

        const screenNames: string[] = [];

        const rowDescriptions = getArrayProperty<RowDescription>(desc.rows) ?? [];
        for (const row of rowDescriptions) {
            const column = getColumnForRow(schema, tables, row);
            if (column === undefined) continue;

            if (isMultiRelationType(column.type)) {
                screenNames.push(arrayScreenName(column.type.items));
            }
        }

        return screenNames;
    }

    public getDescriptor(
        _desc: SimpleTableComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<SimpleTableComponentDescription> {
        return {
            name: "Basic Table",
            description: "A table displaying rows of values",
            img: "componentBasicTable",
            group: "Layout",
            helpUrl: getDocURL("simpleTable"),
            isLegacy: !getFeatureSetting("simpleTable"),
            properties: [
                makeTextPropertyDescriptor(
                    "caption",
                    "Title",
                    "Enter the component's title",
                    false,
                    mutatingScreenKind,
                    {
                        propertySection: PropertySection.Top,
                    }
                ),
                {
                    kind: PropertyKind.Array,
                    label: "Rows",
                    property: { name: "rows" },
                    section: PropertySection.Data,
                    properties: [
                        makeCaptionStringPropertyDescriptor("Data", true, mutatingScreenKind),
                        makeTextPropertyDescriptor("value", "Right", "Enter text", true, mutatingScreenKind, {
                            columnFirst: true,
                            ignoreColumnsFromProperties: ["caption"],
                            isDefaultCaption: true,
                            getAllowedColumns: t =>
                                getNonHiddenColumns(t).filter(
                                    c => isPrimitiveType(c.type) || isMultiRelationType(c.type)
                                ),
                        }),
                    ],
                    allowEmpty: false,
                    allowReorder: true,
                    addItemLabel: "Add Row",
                    style: ArrayPropertyStyle.KeyValue,
                },
                makeTextPropertyDescriptor(
                    "footer",
                    "Footer",
                    "Enter the component's footer",
                    false,
                    mutatingScreenKind,
                    {
                        columnFirst: true,
                        emptyByDefault: true,
                    }
                ),
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }
}
